import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronsUpDown, Search, SearchX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

const SEARCH_DEBOUNCE_MS = 250;
const MAX_VISIBLE_RESULTS = 50;
const MAX_VISIBLE_CHIPS = 2;
const MAX_COLLAPSED_SELECTED = 5;

export interface SearchableFilterOption {
  value: string;
  label: string;
  /** Secondary line shown under the label (e.g. a role or a project code). */
  subLabel?: string;
  /** Extra terms the option should be searchable by, on top of label and subLabel. */
  keywords?: string[];
}

interface SearchableFilterSelectProps {
  options: SearchableFilterOption[];
  /** Labels for selected values that aren't in `options`, e.g. when another filter narrows the list. */
  selectedOptions?: SearchableFilterOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Plural noun used in counts and the empty state, e.g. "resources". */
  itemNoun?: string;
}

interface SearchEntry extends SearchableFilterOption {
  haystack: string;
}

/**
 * Multi-select for filter fields with large option lists: debounced search over
 * the option label plus any extra keywords, a capped result list, and the
 * current selection pinned to the top so it stays reachable.
 */
export function SearchableFilterSelect({
  options,
  selectedOptions,
  value,
  onValueChange,
  placeholder = 'All',
  searchPlaceholder = 'Search...',
  itemNoun = 'options',
}: SearchableFilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showAllSelected, setShowAllSelected] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
      setShowAllSelected(false);
    }
  }, [open]);

  // Lower-cased index so filtering thousands of options stays cheap
  const searchIndex = useMemo<SearchEntry[]>(
    () =>
      options.map((option) => ({
        ...option,
        haystack: [option.label, option.subLabel, ...(option.keywords || [])]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      })),
    [options]
  );

  const optionsByValue = useMemo(() => {
    const map = new Map<string, SearchableFilterOption>();
    selectedOptions?.forEach((option) => map.set(option.value, option));
    searchIndex.forEach((entry) => map.set(entry.value, entry));
    return map;
  }, [searchIndex, selectedOptions]);

  // Every whitespace-separated term must appear, so "anna design" matches across fields
  const matches = useMemo(() => {
    const terms = debouncedQuery.split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];
    return searchIndex.filter((entry) => terms.every((term) => entry.haystack.includes(term)));
  }, [searchIndex, debouncedQuery]);

  const unselected = useMemo(
    () => searchIndex.filter((entry) => !value.includes(entry.value)),
    [searchIndex, value]
  );

  const visibleRows = useMemo(() => {
    const source = debouncedQuery
      ? matches.filter((entry) => !value.includes(entry.value))
      : unselected;
    return source.slice(0, MAX_VISIBLE_RESULTS);
  }, [debouncedQuery, matches, unselected, value]);

  const selectedEntries = useMemo(
    () =>
      value.map((selected) => {
        const entry = optionsByValue.get(selected);
        return {
          value: selected,
          label: entry?.label || selected,
          subLabel: entry?.subLabel,
        };
      }),
    [value, optionsByValue]
  );

  const toggle = useCallback(
    (optionValue: string) => {
      onValueChange(
        value.includes(optionValue)
          ? value.filter((item) => item !== optionValue)
          : [...value, optionValue]
      );
    },
    [value, onValueChange]
  );

  const renderRow = (optionValue: string, label: string, subLabel?: string) => (
    <CommandItem
      key={optionValue}
      value={optionValue}
      onSelect={() => toggle(optionValue)}
      className="px-3 py-2 cursor-pointer"
    >
      <div className="flex items-center gap-2 w-full">
        <Checkbox checked={value.includes(optionValue)} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm truncate block">{label}</span>
          {subLabel && (
            <span className="text-xs text-muted-foreground truncate block">{subLabel}</span>
          )}
        </div>
      </div>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          role="button"
          aria-expanded={open}
          className={cn(
            'border-input w-full flex items-center justify-between min-h-10 h-auto py-2 px-3 text-sm',
            'border rounded-md bg-background cursor-pointer',
            'hover:bg-accent hover:text-accent-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
          )}
        >
          <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0 pr-2">
            {selectedEntries.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              <>
                {selectedEntries.slice(0, MAX_VISIBLE_CHIPS).map((entry) => (
                  <div key={entry.value} onClick={(event) => event.stopPropagation()}>
                    <Badge
                      variant="secondary"
                      onClose={() => onValueChange(value.filter((item) => item !== entry.value))}
                    >
                      {entry.label}
                    </Badge>
                  </div>
                ))}
                {selectedEntries.length > MAX_VISIBLE_CHIPS && (
                  <Badge variant="outline">+{selectedEntries.length - MAX_VISIBLE_CHIPS} more</Badge>
                )}
              </>
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-[340px] p-0" align="start" sideOffset={4} collisionPadding={10}>
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              autoFocus
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="flex h-10 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <CommandList className="max-h-[320px] overflow-y-auto">
            {selectedEntries.length > 0 && (
              <>
                <CommandGroup>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs font-semibold uppercase text-muted-foreground">
                      Selected ({selectedEntries.length})
                    </span>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => onValueChange([])}
                    >
                      Clear all
                    </button>
                  </div>
                  {(showAllSelected
                    ? selectedEntries
                    : selectedEntries.slice(0, MAX_COLLAPSED_SELECTED)
                  ).map((entry) => renderRow(entry.value, entry.label, entry.subLabel))}
                  {selectedEntries.length > MAX_COLLAPSED_SELECTED && (
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left text-xs text-primary hover:underline"
                      onClick={() => setShowAllSelected((previous) => !previous)}
                    >
                      {showAllSelected
                        ? 'Show fewer'
                        : `Show all ${selectedEntries.length} selected`}
                    </button>
                  )}
                </CommandGroup>
                <Separator />
              </>
            )}

            {debouncedQuery && matches.length === 0 ? (
              <div className="py-6 text-center">
                <SearchX className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No {itemNoun} found</p>
                <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
              </div>
            ) : (
              <CommandGroup>
                <div className="px-3 py-2 text-xs text-muted-foreground">
                  {debouncedQuery
                    ? matches.length > MAX_VISIBLE_RESULTS
                      ? `Showing ${MAX_VISIBLE_RESULTS} of ${matches.length.toLocaleString()} matches — refine your search`
                      : `${matches.length} match${matches.length !== 1 ? 'es' : ''}`
                    : unselected.length > MAX_VISIBLE_RESULTS
                      ? `Showing ${MAX_VISIBLE_RESULTS} of ${options.length.toLocaleString()} ${itemNoun} — search to narrow`
                      : `${options.length.toLocaleString()} ${itemNoun} available`}
                </div>
                {visibleRows.map((entry) => renderRow(entry.value, entry.label, entry.subLabel))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
